import { ORDER_SIDE } from "../../shared";
import { z } from "zod";

export enum SimulationCampaignType {
  CEX = "CEX",
  ONCHAIN = "ONCHAIN",
}

export const CexSimulationTargetSchema = z.object({
  campaignType: z.literal(SimulationCampaignType.CEX),
  exchangeAccountId: z.number().int().positive(),
});
export type CexSimulationTarget = z.infer<typeof CexSimulationTargetSchema>;

/** Input target for creating an on-chain simulation (walletAddress derived server-side) */
export const CreateOnchainSimulationTargetSchema = z.object({
  campaignType: z.literal(SimulationCampaignType.ONCHAIN),
  chainId: z.string().min(1),
  dexExchangeId: z.string().min(1),
  baseTokenAddress: z.string().min(1),
  quoteTokenAddress: z.string().min(1),
});
export type CreateOnchainSimulationTarget = z.infer<typeof CreateOnchainSimulationTargetSchema>;

/** Stored on-chain target (includes server-derived walletAddress) */
export const OnchainSimulationTargetSchema = CreateOnchainSimulationTargetSchema.extend({
  walletAddress: z.string().min(1),
});
export type OnchainSimulationTarget = z.infer<typeof OnchainSimulationTargetSchema>;

/** Input target for creating a simulation (CEX or on-chain) */
export const CreateSimulationTargetSchema = z.discriminatedUnion("campaignType", [
  CexSimulationTargetSchema,
  CreateOnchainSimulationTargetSchema,
]);
export type CreateSimulationTarget = z.infer<typeof CreateSimulationTargetSchema>;

/** Stored target (CEX or on-chain, with walletAddress resolved) */
export const SimulationTargetSchema = z.discriminatedUnion("campaignType", [
  CexSimulationTargetSchema,
  OnchainSimulationTargetSchema,
]);
export type SimulationTarget = z.infer<typeof SimulationTargetSchema>;

export const SIMULATION_STATUS = {
  PENDING: "PENDING",
  USER_CANCELED: "USER_CANCELED",
  RUNNING: "RUNNING",
  RUN_FAILED: "RUN_FAILED",
  RUN_COMPLETED: "RUN_COMPLETED",
  ENRICHING_DATA: "ENRICHING_DATA",
  ENRICHMENT_FAILED: "ENRICHMENT_FAILED",
  ENRICHMENT_COMPLETED: "ENRICHMENT_COMPLETED",
} as const;
export type SimulationStatus = (typeof SIMULATION_STATUS)[keyof typeof SIMULATION_STATUS];

export const SIMULATION_ERROR_REASONS = {
  INSUFFICIENT_BALANCE: "INSUFFICIENT_BALANCE",
  TRADE_FAILED: "TRADE_FAILED",
  ORDER_EXECUTION_FAILED: "ORDER_EXECUTION_FAILED",
  BALANCE_FETCH_FAILED: "BALANCE_FETCH_FAILED",
  ORDER_NOT_FOUND: "ORDER_NOT_FOUND",
  ENRICHMENT_FAILED: "ENRICHMENT_FAILED",
  LEADERBOARD_SYNC_TIMEOUT: "LEADERBOARD_SYNC_TIMEOUT",
} as const;
export type SimulationErrorReason = (typeof SIMULATION_ERROR_REASONS)[keyof typeof SIMULATION_ERROR_REASONS];

/**
 * Schema for a single simulation trade entry (one order execution).
 * Uses serializable types (strings) for volume and timestamp to allow direct JSON storage.
 */
export const SimulationTradeEntrySchema = z.object({
  index: z.number().int().min(0),
  orderSide: z.nativeEnum(ORDER_SIDE),
  orderId: z.string(),
  volume: z.string(), // Decimal as string for JSON serialization
  timestamp: z.string(), // ISO date string for JSON serialization
  success: z.boolean(),
  error: z.string().optional(),
});
export type SimulationTradeEntry = z.infer<typeof SimulationTradeEntrySchema>;

export const EnrichedSimulationTradeEntrySchema = SimulationTradeEntrySchema.extend({
  filled: z.string(),
  price: z.string(),
  filledQuote: z.string(),
  feeAmountUSD: z.string().optional(),
});
export type EnrichedSimulationTradeEntry = z.infer<typeof EnrichedSimulationTradeEntrySchema>;

export const LeaderboardStateSchema = z.object({
  rank: z.number().nullable(),
  volumeUSD: z.string(),
  tradedVolumeUSD: z.string(),
  rewardUsd: z.string(),
  tradesCount: z.number(),
});
export type LeaderboardState = z.infer<typeof LeaderboardStateSchema>;

export const PreviousEpochTopTraderSchema = z.object({
  defiAddress: z.string(),
  volumeUSD: z.string(),
  totalTradedVolumeUSD: z.string(),
  rewardTokenAmount: z.string(),
});
export type PreviousEpochTopTrader = z.infer<typeof PreviousEpochTopTraderSchema>;

export const PreviousEpochLeaderboardSchema = z.object({
  totalVolumeUSD: z.string(),
  totalRewardTokenAmount: z.string(),
});
export type PreviousEpochLeaderboard = z.infer<typeof PreviousEpochLeaderboardSchema>;

export const TokenBalanceSnapshotSchema = z.object({
  token: z.string(),
  amount: z.string(),
  amountUsd: z.string(),
});

export const BalanceSnapshotSchema = z.object({
  base: TokenBalanceSnapshotSchema,
  quote: TokenBalanceSnapshotSchema,
});
export type BalanceSnapshot = z.infer<typeof BalanceSnapshotSchema>;

/**
 * Discriminated union schemas for ExecutionResult based on simulation status
 */
export const RunningExecutionResultSchema = z.object({
  status: z.literal(SIMULATION_STATUS.RUNNING),
  initialLeaderboardState: LeaderboardStateSchema,
  initialSpread: z.string().nullable(),
  initialBalance: BalanceSnapshotSchema.nullable().optional(),
  trades: z.array(SimulationTradeEntrySchema),
});

export const SuccessfulExecutionResultSchema = z.object({
  status: z.literal(SIMULATION_STATUS.RUN_COMPLETED),
  initialLeaderboardState: LeaderboardStateSchema,
  initialSpread: z.string().nullable(),
  initialBalance: BalanceSnapshotSchema.nullable().optional(),
  trades: z.array(SimulationTradeEntrySchema),
});

export const FailedExecutionResultSchema = z.object({
  status: z.literal(SIMULATION_STATUS.RUN_FAILED),
  initialLeaderboardState: LeaderboardStateSchema,
  initialSpread: z.string().nullable(),
  initialBalance: BalanceSnapshotSchema.nullable().optional(),
  trades: z.array(SimulationTradeEntrySchema),
  errorReason: z.nativeEnum(SIMULATION_ERROR_REASONS),
  errorMessage: z.string().optional(),
});

export const EnrichingDataExecutionResultSchema = z.object({
  status: z.literal(SIMULATION_STATUS.ENRICHING_DATA),
  initialLeaderboardState: LeaderboardStateSchema,
  initialSpread: z.string().nullable(),
  initialBalance: BalanceSnapshotSchema.nullable().optional(),
  trades: z.union([z.array(SimulationTradeEntrySchema), z.array(EnrichedSimulationTradeEntrySchema)]),
});

export const EnrichmentFailedExecutionResultSchema = z.object({
  status: z.literal(SIMULATION_STATUS.ENRICHMENT_FAILED),
  initialLeaderboardState: LeaderboardStateSchema,
  initialSpread: z.string().nullable(),
  initialBalance: BalanceSnapshotSchema.nullable().optional(),
  trades: z.union([z.array(SimulationTradeEntrySchema), z.array(EnrichedSimulationTradeEntrySchema)]),
  errorReason: z.nativeEnum(SIMULATION_ERROR_REASONS),
  errorMessage: z.string().optional(),
});

export const EnrichmentCompletedExecutionResultSchema = z.object({
  status: z.literal(SIMULATION_STATUS.ENRICHMENT_COMPLETED),
  initialLeaderboardState: LeaderboardStateSchema,
  initialSpread: z.string().nullable(),
  initialBalance: BalanceSnapshotSchema.nullable().optional(),
  finalBalance: BalanceSnapshotSchema.nullable().optional(),
  trades: z.array(EnrichedSimulationTradeEntrySchema),
  finalLeaderboardState: LeaderboardStateSchema,
  finalSpread: z.string().nullable(),
  previousEpochTopTrader: PreviousEpochTopTraderSchema.nullable(),
  previousLeaderboard: PreviousEpochLeaderboardSchema.nullable().optional(),
});

export const ExecutionResultSchema = z.discriminatedUnion("status", [
  RunningExecutionResultSchema,
  SuccessfulExecutionResultSchema,
  FailedExecutionResultSchema,
  EnrichingDataExecutionResultSchema,
  EnrichmentFailedExecutionResultSchema,
  EnrichmentCompletedExecutionResultSchema,
]);

export type ExecutionResult = z.infer<typeof ExecutionResultSchema>;
export type RunningExecutionResult = z.infer<typeof RunningExecutionResultSchema>;
export type SuccessfulExecutionResult = z.infer<typeof SuccessfulExecutionResultSchema>;
export type FailedExecutionResult = z.infer<typeof FailedExecutionResultSchema>;
export type EnrichingDataExecutionResult = z.infer<typeof EnrichingDataExecutionResultSchema>;
export type EnrichmentFailedExecutionResult = z.infer<typeof EnrichmentFailedExecutionResultSchema>;
export type EnrichmentCompletedExecutionResult = z.infer<typeof EnrichmentCompletedExecutionResultSchema>;

export const CpcSimulationDataSchema = z.object({
  id: z.string(),
  campaignType: z.nativeEnum(SimulationCampaignType),
  campaignName: z.string(),
  epochId: z.number().nullable(),
  rule: z.string().nullable(),
  exchangeAccountId: z.number().nullable(),
  exchangeAccountName: z.string().nullable(),
  exchangeId: z.string().nullable(),
  chainId: z.string().nullable(),
  dexExchangeId: z.string().nullable(),
  walletAddress: z.string().nullable(),
  baseTokenAddress: z.string().nullable(),
  quoteTokenAddress: z.string().nullable(),
  pairSymbol: z.string(),
  tradeSizeUsd: z.number(),
  tradeCount: z.number(),
  epochExpectedTradedVolume: z.number().nullable(),
  epochMaxRewardUsd: z.number().nullable(),
  scheduledStartAt: z.string(),
  status: z.nativeEnum(SIMULATION_STATUS),
  executionResult: ExecutionResultSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CpcSimulationData = z.infer<typeof CpcSimulationDataSchema>;

export const CpcListSimulationsResponseSchema = z.object({
  simulations: z.array(CpcSimulationDataSchema),
});
export type CpcListSimulationsResponse = z.infer<typeof CpcListSimulationsResponseSchema>;

export const CpcProfitabilitySchema = z.object({
  costUsd: z.number(),
  volumeGeneratedUsd: z.number(),
  costPer1000VolumeUsd: z.number().nullable(),
  epochExpectedTradedVolume: z.number().nullable(),
  epochMaxRewardUsd: z.number().nullable(),
  rewardPer1000VolumeUsd: z.number().nullable(),
  pnlPer1000VolumeUsd: z.number().nullable(),
});
export type CpcProfitability = z.infer<typeof CpcProfitabilitySchema>;

export const CpcCancelSimulationResponseSchema = z.object({
  success: z.boolean(),
});
export type CpcCancelSimulationResponse = z.infer<typeof CpcCancelSimulationResponseSchema>;
